import { useCallback, useState } from 'react';
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfDay,
  format,
  getDaysInMonth,
  isToday,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchPaymentsLedger,
  getChannelLabel,
  isStripePayment,
  type PaymentLedgerEntry,
} from '../lib/paymentsLedger';
import { isStoredCreditRedemption } from './usePaymentsByTypeThisMonth';

/**
 * Same taxonomy (and display order) as the invoices "Payments this month"
 * widget, plus Wallet so stored credit doesn't hide inside Other.
 */
export const CASH_PAYMENT_TYPES = [
  { type: 'Stripe', color: '#635bff' },
  { type: 'Credit', color: '#3b82f6' },
  { type: 'Cash', color: '#10b981' },
  { type: 'Bank transfer', color: '#f59e0b' },
  { type: 'Gift card', color: '#ec4899' },
  { type: 'Wallet', color: '#14b8a6' },
  { type: 'Check', color: '#8b5cf6' },
  { type: 'Other', color: '#6b7280' },
] as const;

export type CashPaymentType = (typeof CASH_PAYMENT_TYPES)[number]['type'];

const TYPE_COLORS = Object.fromEntries(
  CASH_PAYMENT_TYPES.map(({ type, color }) => [type, color])
) as Record<CashPaymentType, string>;

export interface CashDayRow {
  date: string;
  label: string;
  amounts: Record<CashPaymentType, number>;
  total: number;
  isToday: boolean;
}

export interface CashChannelRow {
  key: string;
  label: string;
  /** Same color this money gets in the daily chart. */
  color: string;
  amount: number;
  count: number;
  /** 0..1 share of the source group's total. */
  share: number;
}

export type CashSourceKey =
  | 'invoices'
  | 'wallet_topups'
  | 'gift_card_purchases'
  | 'credit_redeemed';

export interface CashSourceGroup {
  key: CashSourceKey;
  label: string;
  total: number;
  count: number;
  channels: CashChannelRow[];
}

export interface CashFeedRow {
  id: string;
  customerName: string;
  channelLabel: string;
  amount: number;
  tip: number;
  time: string;
  refunded: boolean;
}

export interface CashFeedDay {
  date: string;
  label: string;
  isToday: boolean;
  rows: CashFeedRow[];
}

export interface CashCollectedSummary {
  /** Real new cash in the door — excludes stored-credit redemptions. */
  cashCollected: number;
  /** Wallet / gift card credit spent on invoices (liability converting, not new cash). */
  creditRedeemed: number;
  /** cashCollected + creditRedeemed; what invoices actually received. */
  gross: number;
  principal: number;
  tips: number;
  surcharges: number;
  stripeFees: number;
  netAfterFees: number;
  txCount: number;
  collectedToday: number;
  avgPerDay: number;
  projectedMonthEnd: number;
  /** Gross collected by this point (same day-of-month) last month. */
  lastMonthSamePoint: number;
  lastMonthTotal: number;
  refundsTotal: number;
  refundsCount: number;
  voidsCount: number;
}

const EMPTY_SUMMARY: CashCollectedSummary = {
  cashCollected: 0,
  creditRedeemed: 0,
  gross: 0,
  principal: 0,
  tips: 0,
  surcharges: 0,
  stripeFees: 0,
  netAfterFees: 0,
  txCount: 0,
  collectedToday: 0,
  avgPerDay: 0,
  projectedMonthEnd: 0,
  lastMonthSamePoint: 0,
  lastMonthTotal: 0,
  refundsTotal: 0,
  refundsCount: 0,
  voidsCount: 0,
};

/** Mirrors the invoices widget's classifier, with Wallet split out of Other. */
function paymentTypeFor(entry: PaymentLedgerEntry): CashPaymentType {
  if (entry.channel === 'wallet') return 'Wallet';
  if (entry.gift_card_id || entry.channel === 'gift_card') return 'Gift card';
  if (isStripePayment(entry)) return 'Stripe';
  if (entry.channel === 'cash' || entry.method === 'cash') return 'Cash';
  if (entry.channel === 'bank_transfer' || entry.method === 'bank_transfer') return 'Bank transfer';
  if (entry.channel === 'check' || entry.method === 'check') return 'Check';
  if (
    entry.channel === 'credit' ||
    entry.channel === 'debit' ||
    entry.method === 'credit_card' ||
    entry.method === 'debit_card'
  ) {
    return 'Credit';
  }
  return 'Other';
}

function emptyAmounts(): Record<CashPaymentType, number> {
  return Object.fromEntries(CASH_PAYMENT_TYPES.map(({ type }) => [type, 0])) as Record<
    CashPaymentType,
    number
  >;
}

/** Same definition as the hero tile: completed payments with a positive gross amount. */
function isCollected(entry: PaymentLedgerEntry): boolean {
  return entry.status === 'completed' && (entry.amount ?? 0) > 0;
}

/**
 * What the money was collected FOR. Wallet top-ups and gift card purchases are
 * cash-in that isn't invoice revenue yet — splitting them out keeps the invoice
 * group honest and makes the stored-credit double-count visible instead of
 * silently inflating one list.
 */
function paymentSource(entry: PaymentLedgerEntry): CashSourceKey {
  if (isStoredCreditRedemption(entry)) return 'credit_redeemed';
  // Only the Stripe webhook writes this reference (portal self-serve top-ups).
  if (entry.reference === 'Wallet top-up') return 'wallet_topups';
  // Purchase rows carry the gift card but settle nothing; redemptions have the
  // gift_card channel and/or an invoice/booking/sale link.
  if (
    entry.gift_card_id &&
    entry.channel !== 'gift_card' &&
    !entry.invoice_id &&
    !entry.booking_id &&
    !entry.product_sale_id
  ) {
    return 'gift_card_purchases';
  }
  return 'invoices';
}

const SOURCE_LABELS: Record<CashSourceKey, string> = {
  invoices: 'Collected on invoices',
  wallet_topups: 'Wallet top-ups',
  gift_card_purchases: 'Gift card purchases',
  credit_redeemed: 'Stored credit redeemed',
};

const SOURCE_ORDER: CashSourceKey[] = [
  'invoices',
  'wallet_topups',
  'gift_card_purchases',
  'credit_redeemed',
];

/** Normalized channel key; older rows only have a method, top-ups only Stripe IDs. */
function channelKeyFor(entry: PaymentLedgerEntry): string {
  if (entry.channel) return entry.channel;
  if (isStripePayment(entry)) return 'card_online';
  switch (entry.method) {
    case 'cash':
      return 'cash';
    case 'bank_transfer':
      return 'bank_transfer';
    case 'check':
      return 'check';
    case 'credit_card':
      return 'credit';
    case 'debit_card':
      return 'debit';
    default:
      return 'other';
  }
}

/** Stored credit spent on invoices reads better as "credit" than as a raw channel. */
function channelLabelFor(source: CashSourceKey, channelKey: string): string {
  if (source === 'credit_redeemed' && channelKey === 'wallet') return 'Wallet credit';
  if (source === 'credit_redeemed' && channelKey === 'gift_card') return 'Gift card credit';
  return getChannelLabel(channelKey);
}

/**
 * Everything the Cash collected modal shows: month-to-date gross with prior-month
 * comparison, per-day series stacked by method group, channel breakdown,
 * tip/surcharge/fee composition, refunds, and a day-grouped payment feed.
 */
export function useCashCollectedDetails() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [summary, setSummary] = useState<CashCollectedSummary>(EMPTY_SUMMARY);
  const [days, setDays] = useState<CashDayRow[]>([]);
  const [sources, setSources] = useState<CashSourceGroup[]>([]);
  const [feedDays, setFeedDays] = useState<CashFeedDay[]>([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!businessId) {
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setSources([]);
      setFeedDays([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      setMonthLabel(format(now, 'MMMM yyyy'));

      // One fetch covers both months; the view already excludes pending rows.
      const entries = await fetchPaymentsLedger(businessId, {
        start: lastMonthStart.toISOString(),
        end: endOfDay(now).toISOString(),
      });

      const monthStartMs = monthStart.getTime();
      const thisMonth = entries.filter((e) => new Date(e.created_at).getTime() >= monthStartMs);
      const lastMonth = entries.filter((e) => new Date(e.created_at).getTime() < monthStartMs);

      const collected = thisMonth.filter(isCollected);

      let gross = 0;
      let creditRedeemed = 0;
      let tips = 0;
      let surcharges = 0;
      let stripeFees = 0;
      let collectedToday = 0;
      const byDay = new Map<string, Record<CashPaymentType, number>>();
      const bySource = new Map<
        CashSourceKey,
        Map<string, { amount: number; count: number; color: string }>
      >();

      for (const entry of collected) {
        const amount = entry.amount ?? 0;
        const isRedemption = isStoredCreditRedemption(entry);
        gross += amount;
        if (isRedemption) creditRedeemed += amount;
        tips += entry.tip_amount ?? 0;
        surcharges += entry.surcharge_amount ?? 0;
        stripeFees += entry.stripe_fee ?? 0;

        const created = new Date(entry.created_at);
        // Headline stats are real cash only; redemptions aren't new money.
        if (!isRedemption && isToday(created)) collectedToday += amount;

        const dayKey = format(created, 'yyyy-MM-dd');
        const day = byDay.get(dayKey) ?? emptyAmounts();
        day[paymentTypeFor(entry)] += amount;
        byDay.set(dayKey, day);

        const source = paymentSource(entry);
        const channelKey = channelKeyFor(entry);
        let channelMap = bySource.get(source);
        if (!channelMap) {
          channelMap = new Map();
          bySource.set(source, channelMap);
        }
        // Color from the same classifier the chart uses, so a row and its bars
        // always match (e.g. gift card purchases are pink even though the
        // customer paid by card).
        const channel =
          channelMap.get(channelKey) ??
          { amount: 0, count: 0, color: TYPE_COLORS[paymentTypeFor(entry)] };
        channel.amount += amount;
        channel.count += 1;
        channelMap.set(channelKey, channel);
      }

      // Prior month, cut off at the same point in the month for a fair trend.
      const elapsedDays = differenceInCalendarDays(now, monthStart) + 1;
      let lastMonthSamePoint = 0;
      let lastMonthTotal = 0;
      for (const entry of lastMonth) {
        if (!isCollected(entry) || isStoredCreditRedemption(entry)) continue;
        const amount = entry.amount ?? 0;
        lastMonthTotal += amount;
        const dayOfMonth = differenceInCalendarDays(new Date(entry.created_at), lastMonthStart) + 1;
        if (dayOfMonth <= elapsedDays) lastMonthSamePoint += amount;
      }

      let refundsTotal = 0;
      let refundsCount = 0;
      let voidsCount = 0;
      for (const entry of thisMonth) {
        if (entry.status === 'refunded') {
          refundsTotal += Math.abs(entry.amount ?? 0);
          refundsCount += 1;
        } else if (entry.status === 'voided') {
          voidsCount += 1;
        }
      }

      const dayRows: CashDayRow[] = eachDayOfInterval({
        start: startOfDay(monthStart),
        end: startOfDay(now),
      }).map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const amounts = byDay.get(key) ?? emptyAmounts();
        return {
          date: key,
          label: format(day, 'MMM d'),
          amounts,
          total: Object.values(amounts).reduce((sum, v) => sum + v, 0),
          isToday: isToday(day),
        };
      });

      const sourceGroups: CashSourceGroup[] = SOURCE_ORDER.flatMap((sourceKey) => {
        const channelMap = bySource.get(sourceKey);
        if (!channelMap || channelMap.size === 0) return [];
        let total = 0;
        let count = 0;
        for (const value of channelMap.values()) {
          total += value.amount;
          count += value.count;
        }
        const channelRows: CashChannelRow[] = Array.from(channelMap.entries())
          .map(([key, value]) => ({
            key,
            label: channelLabelFor(sourceKey, key),
            color: value.color,
            amount: value.amount,
            count: value.count,
            share: total > 0 ? value.amount / total : 0,
          }))
          .sort((a, b) => b.amount - a.amount);
        return [{ key: sourceKey, label: SOURCE_LABELS[sourceKey], total, count, channels: channelRows }];
      });

      // Feed: completed + refunded rows this month, newest first, grouped by day.
      const feedEntries = thisMonth
        .filter((e) => isCollected(e) || e.status === 'refunded')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      const feedByDay = new Map<string, CashFeedDay>();
      for (const entry of feedEntries) {
        const created = new Date(entry.created_at);
        const key = format(created, 'yyyy-MM-dd');
        let day = feedByDay.get(key);
        if (!day) {
          day = {
            date: key,
            label: format(created, 'EEE, MMM d'),
            isToday: isToday(created),
            rows: [],
          };
          feedByDay.set(key, day);
        }
        day.rows.push({
          id: entry.id,
          customerName: entry.customer_name?.trim() || 'No customer',
          channelLabel: getChannelLabel(entry.channel || entry.method),
          amount: entry.amount ?? 0,
          tip: entry.tip_amount ?? 0,
          time: format(created, 'h:mm a'),
          refunded: entry.status === 'refunded',
        });
      }

      const cashCollected = gross - creditRedeemed;
      const avgPerDay = elapsedDays > 0 ? cashCollected / elapsedDays : 0;

      setSummary({
        cashCollected,
        creditRedeemed,
        gross,
        principal: cashCollected - tips - surcharges,
        tips,
        surcharges,
        stripeFees,
        netAfterFees: cashCollected - stripeFees,
        txCount: collected.length,
        collectedToday,
        avgPerDay,
        projectedMonthEnd: avgPerDay * getDaysInMonth(now),
        lastMonthSamePoint,
        lastMonthTotal,
        refundsTotal,
        refundsCount,
        voidsCount,
      });
      setDays(dayRows);
      setSources(sourceGroups);
      setFeedDays(Array.from(feedByDay.values()));
    } catch (err) {
      console.error('Error loading cash collected details:', err);
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setSources([]);
      setFeedDays([]);
      setError('Could not load cash details.');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  return { summary, days, sources, feedDays, monthLabel, loading, error, load };
}
